import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  Checkbox, 
  FormControlLabel, 
  CircularProgress, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Alert,
  Fade,
  Avatar,
  useTheme,
  useMediaQuery,
  Pagination
} from '@mui/material';
import { 
  CalendarToday, 
  DirectionsCar, 
  LocationOn, 
  Schedule, 
  ChevronLeft, 
  ChevronRight,
  ElectricCar,
  CheckCircle,
  AccessTime
} from '@mui/icons-material';
import { MovilidadElectricaProvider } from '../../context/MovilidadElectricaContext';
import { getPlacesByUserName } from '../../services/userService';
import { getApartmentById, getMyApartment } from '../../services/apartmentService';
import { getVehiclesByUserAndPlace } from '../../services/vehicleService';
import { getAvailabilityByDate, createBooking, getBookingsByVehicleAndDate, getSuggestedBookings } from '../../services/reservasService';
import { getParkers } from '../../services/parkerService';
import { getActiveTariff } from '../../services/tariffService';
import { getWalletByUserId } from '../../services/financeService';
import { AuthContext } from '../../context/AuthContext';
import SaldoInsuficienteAlert, { puedeReservar } from '../../components/Billetera/SaldoInsuficienteAlert';

const ReservasContent = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isAdminUser = user?.is_admin;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [filters, setFilters] = useState({
    selectedDay: null,
    selectedStartTime: '',
    selectedEndTime: '',
    selectedParker: '',
    selectedVehicle: '',
    selectedBlock: '',
  });
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [userData, setUserData] = useState(null);
  const [apartmentData, setApartmentData] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [availableStartTimes, setAvailableStartTimes] = useState([]);
  const [availableEndTimes, setAvailableEndTimes] = useState([]);
  const [vehicleBookings, setVehicleBookings] = useState([]);
  const [allParkers, setAllParkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHorarios, setLoadingHorarios] = useState(false);
  const [error, setError] = useState(null);
  const [suggestedBookings, setSuggestedBookings] = useState([]);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(null);
  const [selectOpen, setSelectOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [activeTariff, setActiveTariff] = useState(null);
  // null = aún no se conoce el saldo; no se bloquea el botón por un fallo al consultarlo
  // (el backend igual rechaza la reserva si no hay saldo).
  const [walletBalance, setWalletBalance] = useState(null);
  const sinSaldo = walletBalance !== null && !puedeReservar(walletBalance);
  
  const totalPages = Math.ceil(suggestedBookings.length / itemsPerPage);
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedBookings = suggestedBookings.slice(startIndex, startIndex + itemsPerPage);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };
 
  const formatTo12Hour = (hour) => {
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h}:00 ${ampm}`;
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const userDetailsResponse = await getPlacesByUserName();
        const userDetails = userDetailsResponse.data[0];
        if (!userDetails || !userDetails.id) throw new Error("Datos de usuario no encontrados.");
        
        setUserData(userDetails);
        const userId = userDetails.id;
        if (!userDetails.places || userDetails.places.length === 0) {
          throw new Error("El usuario no tiene una sede asignada.");
        }
        const placeId = userDetails.places[0].id;

        const [userVehiclesResponse, parkersResponse, activeTariffResponse] = await Promise.all([
          getVehiclesByUserAndPlace({ user_id: userId, place_id: placeId }),
          getParkers({ place_id: placeId }),
          getActiveTariff(placeId)
        ]);

        getWalletByUserId(userId)
          .then((wallet) => setWalletBalance(parseFloat(wallet?.balance) || 0))
          .catch((walletError) => console.error('Error al cargar la billetera:', walletError));
          
          setVehicles(userVehiclesResponse.data);
          setAllParkers(parkersResponse.data);
          setActiveTariff(activeTariffResponse);

        const apartmentAssignment = await getMyApartment();
        if (apartmentAssignment && apartmentAssignment.apartment_id) {
          const apartmentDetails = await getApartmentById(apartmentAssignment.apartment_id);
          setApartmentData({
            ...apartmentAssignment,
            apartment: { ...apartmentDetails, number: apartmentDetails.matricula }
          });
        } else {
          setError("No tienes un apartamento asignado. Por favor, contacta al administrador.");
        }

      } catch (err) {
        setError('Error al cargar los datos iniciales.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (user && !isAdminUser) {
      fetchInitialData();
    } else if (isAdminUser) {
      setLoading(false);
    }
  }, [user, isAdminUser]);

  useEffect(() => {
    const fetchSuggestedBookings = async () => {
      try {
        const response = await getSuggestedBookings();
        setSuggestedBookings(response.data);
      } catch (err) {
        console.error('Error al cargar las reservas sugeridas:', err);
      }
    };

    if (!isAdminUser) {
      fetchSuggestedBookings();
    }
  }, [isAdminUser]);

  useEffect(() => {
    const fetchAvailability = async () => {
      if (!filters.selectedDay || !filters.selectedParker) {
        setAvailableStartTimes([]);
        setAvailableEndTimes([]);
        return;
      }
      
      try {
        setLoadingHorarios(true);
        const selectedParkerObject = allParkers.find(p => p.parker_id === filters.selectedParker);
        if (!selectedParkerObject) return;

        const location = selectedParkerObject.location;
        const formattedDate = filters.selectedDay.toISOString().split('T')[0];
        const availabilityResponse = await getAvailabilityByDate(formattedDate, location);
        const availability = availabilityResponse.data.availability;
        
        let availableHours = availability[location] ? availability[location][formattedDate] || [] : [];
        
        const today = new Date();
        if (filters.selectedDay && filters.selectedDay.toDateString() === today.toDateString()) {
          const currentHour = today.getHours();
          availableHours = availableHours.filter(hour => hour >= currentHour);
        }

        if (vehicleBookings.length > 0) {
          const bookedHours = new Set();
          vehicleBookings.forEach(booking => {
            const startHour = new Date(booking.start_date).getHours();
            const endHour = new Date(booking.end_date).getHours();
            for (let i = startHour; i < endHour; i++) {
              bookedHours.add(i);
            }
          });
          availableHours = availableHours.filter(hour => !bookedHours.has(hour));
        }
        
        setAvailableStartTimes(availableHours);

      } catch (err) {
        setError('Error al cargar los horarios.');
        console.error(err);
      } finally {
        setLoadingHorarios(false);
      }
    };
    fetchAvailability();
  }, [filters.selectedDay, filters.selectedParker, vehicleBookings]);

  useEffect(() => {
    if (filters.selectedStartTime !== '') {
      const startHour = parseInt(filters.selectedStartTime, 10);
      const possibleEndTimes = [];
      for (let i = startHour + 1; i <= 24; i++) {
        if (availableStartTimes.includes(i)) {
          possibleEndTimes.push(i);
        } else {
          break;
        }
      }
      setAvailableEndTimes(possibleEndTimes);
    } else {
      setAvailableEndTimes([]);
    }
  }, [filters.selectedStartTime, availableStartTimes]);

  useEffect(() => {
    const fetchVehicleBookings = async () => {
      if (filters.selectedVehicle && filters.selectedDay) {
        try {
          const formattedDate = filters.selectedDay.toISOString().split('T')[0];
          const response = await getBookingsByVehicleAndDate(filters.selectedVehicle, formattedDate);
          setVehicleBookings(response.data);
        } catch (err) {
          console.error("Error al obtener las reservas del vehículo:", err);
          setVehicleBookings([]);
        }
      } else {
        setVehicleBookings([]);
      }
    };
    fetchVehicleBookings();
  }, [filters.selectedVehicle, filters.selectedDay]);

  const handlePrevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const handleNextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value,
    }));
  };

  const handleParkerChange = (event) => {
    const { value } = event.target;
    setFilters(prev => ({
      ...prev,
      selectedParker: value,
      selectedStartTime: '',
      selectedEndTime: '',
      selectedBlock: '',
    }));
  };

const handleBlockChange = (event) => {
  const selectedValue = event.target.value;
  
  if (!selectedValue || selectedValue === "") {
    setFilters(prev => ({
      ...prev,
      selectedBlock: "",
      selectedStartTime: '',
      selectedEndTime: '',
    }));
    return;
  }

  // Parse the block value (e.g., "6-8" or "20-6")
  const [startStr, endStr] = selectedValue.split("-").map(s => s.trim());
  const startHour = parseInt(startStr, 10);
  const endHour = parseInt(endStr, 10);

  setFilters(prev => ({
    ...prev,
    selectedBlock: selectedValue,
    selectedStartTime: String(startHour),
    selectedEndTime: String(endHour),
  }));
};


  const renderCalendar = () => {
    const month = currentDate.getMonth();
    const year = currentDate.getFullYear();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <Box 
          key={`empty-${i}`} 
          sx={{ 
            aspectRatio: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        />
      );
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      const dayDate = new Date(year, month, i);
      const isPastDay = dayDate < today;
      const isSelected = filters.selectedDay && dayDate.toDateString() === filters.selectedDay.toDateString();
      
      days.push(
        <Box
          key={i}
          onClick={() => !isPastDay && setFilters(prev => ({ 
            ...prev, 
            selectedDay: dayDate, 
            selectedStartTime: '', 
            selectedEndTime: '', 
            selectedBlock: '',
            selectedParker: '' 
          }))}
          sx={{
            aspectRatio: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: isPastDay ? 'not-allowed' : 'pointer',
            borderRadius: 2,
            fontSize: { xs: '0.875rem', sm: '1rem' },
            fontWeight: isSelected ? 700 : 500,
            color: isPastDay ? '#ccc' : isSelected ? '#fff' : '#002642',
            backgroundColor: isSelected ? '#002642' : 'transparent',
            border: isSelected ? '2px solid #002642' : '1px solid transparent',
            transition: 'all 0.2s ease',
            '&:hover': !isPastDay ? {
              backgroundColor: isSelected ? '#00577A' : 'rgba(0, 38, 66, 0.08)',
              transform: 'scale(1.05)'
            } : {}
          }}
        >
          {i}
        </Box>
      );
    }
    return days;
  };

  const handleReserveClick = async () => {
    if (!filters.selectedDay || !filters.selectedStartTime || !filters.selectedEndTime || !filters.selectedParker || !filters.selectedVehicle || !termsAccepted) {
      alert('Por favor, complete todos los campos y acepte los términos.');
      return;
    }
    if (sinSaldo) {
      alert('No puedes reservar sin saldo en tu billetera. Recarga para continuar.');
      return;
    }
    
    const startDate = new Date(filters.selectedDay);
    startDate.setHours(parseInt(filters.selectedStartTime, 10), 0, 0, 0);
    
    const endDate = new Date(filters.selectedDay);
    endDate.setHours(parseInt(filters.selectedEndTime, 10), 0, 0, 0);

    if (endDate <= startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }

    const toISOStringWithTimezone = (date) => {
      const pad = (num) => String(num).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
    };

    const startDateTimeString = toISOStringWithTimezone(startDate);
    const endDateTimeString = toISOStringWithTimezone(endDate);
    const formattedDate = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;

    const durationInHours = (endDate - startDate) / (1000 * 60 * 60);
    const estimatedKwh = durationInHours * 7.4;

    const bookingData = {
      vehicle_id: filters.selectedVehicle,
      parker_id: parseInt(filters.selectedParker, 10),
      start_date: startDateTimeString,
      end_date: endDateTimeString,
      booking_date: formattedDate,
      booking_time: `${String(filters.selectedStartTime).padStart(2, '0')}:00:00`,
      kwh_consumed: estimatedKwh,
    };
  
    try {
      await createBooking(bookingData);
      alert('Reserva creada con éxito!');
      navigate('/movilidadElectrica/mis-reservas');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Error al crear la reserva.';
      alert(errorMessage);
      console.error(err);
    }
  };

  const handleSuggestedBookingClick = (booking) => {
      const startTime = new Date(booking.start_time);
      const endTime = new Date(booking.end_time);

    // Asume que las reservas sugeridas son para el día actual o futuro
    const bookingDate = new Date(booking.start_time);
    bookingDate.setHours(0, 0, 0, 0); // Normaliza la fecha para la comparación

    const startHour = startTime.getHours();
    if (!availableStartTimes.includes(startHour)) {
      setAvailableStartTimes(prev => [...prev, startHour].sort((a, b) => a - b));
    }

    setFilters({
      ...filters,
      selectedDay: bookingDate,
      selectedParker: booking.parker_id,
      selectedStartTime: startHour.toString(),
      selectedEndTime: endTime.getHours().toString(),
      selectedBlock: `${startHour}-${endTime.getHours()}`,
    });
    setSelectedSuggestionIndex(booking.id);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} sx={{ color: '#002642' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
        {error}
      </Alert>
    );
  }

  if (isAdminUser) {
    return (
      <Card sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center', borderRadius: 3 }}>
        <ElectricCar sx={{ fontSize: 64, color: '#002642', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#002642', mb: 2 }}>
          Funcionalidad Restringida
        </Typography>
        <Typography variant="body1" sx={{ color: '#75787B' }}>
          La reserva de cargadores es una funcionalidad disponible únicamente para usuarios residentes.
        </Typography>
      </Card>
    );
  }

  return (
    <Box>
      {/* User Info Header */}
      <Card sx={{ mb: 3, p: { xs: 2, sm: 3 }, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 38, 66, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <LocationOn sx={{ color: '#002642', fontSize: '1.5rem' }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: '#002642' }}>
                {apartmentData ? `Apartamento ${apartmentData.apartment.number}` : 'No asignado'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#75787B' }}>
                Usuario: {userData ? userData.username : 'N/A'}
              </Typography>
            </Box>
          </Box>
          <Avatar sx={{ bgcolor: '#002642', width: 48, height: 48 }}>
            {userData ? userData.username.substring(0, 2).toUpperCase() : 'NA'}
          </Avatar>
        </Box>
      </Card>

      {sinSaldo && <SaldoInsuficienteAlert balance={walletBalance} sx={{ mb: 3 }} />}

      {/* Alerta de Tarifa Activa */}
      {activeTariff && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
          El precio actual por kWh es de ${activeTariff.price_per_kwh}.
        </Alert>
      )}

      {/* Turnos Sugeridos */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
            <AccessTime sx={{ color: '#002642', fontSize: '1.5rem' }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#002642' }}>
              Turnos Sugeridos
            </Typography>
          </Box>
          
          {suggestedBookings.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AccessTime sx={{ fontSize: 48, color: '#75787B', mb: 2 }} />
              <Typography variant="h6" sx={{ color: '#002642', mb: 1 }}>
                No hay turnos sugeridos disponibles
              </Typography>
              <Typography variant="body2" sx={{ color: '#75787B' }}>
                Seleccione manualmente su fecha y horario preferido
              </Typography>
            </Box>
          ) : isMobile ? (
            <Grid container spacing={2}>
              {paginatedBookings.map((booking, index) => {
                const parker = allParkers.find(p => p.parker_id === booking.parker_id);
                const startTime = new Date(booking.start_time);
                const endTime = new Date(booking.end_time);
                const isNightSlot = startTime.getHours() === 20 && endTime.getHours() === 6;

                return (
                  <Grid item xs={12} key={index}>
                    <Card
                      onClick={() => handleSuggestedBookingClick({ ...booking, id: index })}
                      sx={{
                        cursor: 'pointer',
                        border: selectedSuggestionIndex === index ? '2px solid #002642' : '1px solid rgba(0, 38, 66, 0.1)',
                        backgroundColor: selectedSuggestionIndex === index ? 'rgba(0, 38, 66, 0.04)' : 'transparent',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          borderColor: '#002642',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 4px 20px rgba(0, 38, 66, 0.15)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#002642' }}>
                            {parker ? parker.location : `Celda ${booking.parker_id}`}
                          </Typography>
                          {isNightSlot && (
                            <Chip label="Nocturno" size="small" color="primary" />
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ color: '#75787B' }}>
                          {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - {endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#002642' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Celda</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Fecha</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Hora de Inicio</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Hora de Fin</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 700 }}>Tipo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {suggestedBookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>
                        <AccessTime sx={{ fontSize: 48, color: '#75787B', mb: 1 }} />
                        <Typography variant="body1" sx={{ color: '#75787B' }}>
                          No hay turnos sugeridos disponibles
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedBookings.map((booking, index) => {
                    const parker = allParkers.find(p => p.parker_id === booking.parker_id);
                    const startTime = new Date(booking.start_time);
                    const endTime = new Date(booking.end_time);
                    const isNightSlot = startTime.getHours() === 20 && endTime.getHours() === 6;

                    return (
                      <TableRow
                        key={index}
                        onClick={() => handleSuggestedBookingClick({ ...booking, id: index })}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: selectedSuggestionIndex === index ? 'rgba(0, 38, 66, 0.04)' : 'transparent',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 38, 66, 0.08)'
                          },
                          transition: 'background-color 0.2s ease'
                        }}
                      >
                        <TableCell sx={{ fontWeight: selectedSuggestionIndex === index ? 700 : 400 }}>
                          {parker ? parker.location : booking.parker_id}
                        </TableCell>
                        <TableCell>
                          {startTime.toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {startTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                        <TableCell>
                          {endTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </TableCell>
                        <TableCell>
                          {isNightSlot ? (
                            <Chip label="Nocturno" size="small" color="primary" />
                          ) : (
                            <Chip label="Diurno" size="small" color="default" />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  }))
                }
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>
      {/* Layout principal */}
      <Grid container spacing={4}>
        {/* Calendario */}
        <Grid item xs={12} lg={4}>
          <Card sx={{ borderRadius: 3, height: 'fit-content', position: 'sticky', top: 20 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <CalendarToday sx={{ color: '#002642', fontSize: '1.5rem' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#002642' }}>
                  Seleccionar Fecha
                </Typography>
              </Box>
              
              {/* Calendar Header */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                mb: 2,
                p: 2,
                backgroundColor: 'rgba(0, 38, 66, 0.04)',
                borderRadius: 2
              }}>
                <IconButton onClick={handlePrevMonth} size="small">
                  <ChevronLeft />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#002642' }}>
                  {currentDate.toLocaleString('es-ES', { month: 'long', year: 'numeric' })}
                </Typography>
                <IconButton onClick={handleNextMonth} size="small">
                  <ChevronRight />
                </IconButton>
              </Box>

              {/* Calendar Grid */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                gap: 1,
                mb: 2
              }}>
                {['D', 'L', 'M', 'M', 'J', 'V', 'S'].map((day, index) => (
                  <Box
                    key={index}
                    sx={{
                      textAlign: 'center',
                      py: 1,
                      fontWeight: 700,
                      fontSize: '0.875rem',
                      color: '#75787B'
                    }}
                  >
                    {day}
                  </Box>
                ))}
                {renderCalendar()}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Formulario de Reserva */}
        <Grid item xs={12} lg={8}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                <Schedule sx={{ color: '#002642', fontSize: '1.5rem' }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#002642' }}>
                  Detalles de Reserva
                </Typography>
              </Box>

              {/* Grid para selectores */}
              <Grid container spacing={3}>
                {/* Selector de Vehículo */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="vehicle-select-label">Seleccione su vehículo</InputLabel>
                    <Select
                      labelId="vehicle-select-label"
                      name="selectedVehicle"
                      value={filters.selectedVehicle}
                      onChange={handleFilterChange}
                      disabled={!vehicles || vehicles.length === 0}
                      label="Seleccione su vehículo"
                      startAdornment={<DirectionsCar sx={{ mr: 1, color: '#75787B' }} />}
                    >
                      <MenuItem value="">
                        <em>Seleccione su vehículo</em>
                      </MenuItem>
                      {vehicles && vehicles.map((vehicle) => (
                        <MenuItem key={vehicle.vehicle_id} value={vehicle.vehicle_id}>
                          {vehicle.model} - {vehicle.plate}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Selector de Celda */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel id="parker-select-label">Seleccione la celda</InputLabel>
                    <Select
                      labelId="parker-select-label"
                      name="selectedParker"
                      value={filters.selectedParker}
                      onChange={handleParkerChange}
                      disabled={!filters.selectedDay}
                      label="Seleccione la celda"
                      startAdornment={<LocationOn sx={{ mr: 1, color: '#75787B' }} />}
                    >
                      <MenuItem value="">
                        <em>Seleccione la celda</em>
                      </MenuItem>
                      {allParkers.map((parker) => (
                        <MenuItem key={parker.parker_id} value={parker.parker_id}>
                          {parker.location}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Selector de Bloque Horario */}
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="block-select-label">Seleccione el bloque horario</InputLabel>
                    <Select
                      labelId="block-select-label"
                      id="block-select"
                      value={filters.selectedBlock || ""}
                      open={selectOpen}
                      onOpen={() => setSelectOpen(true)}
                      onClose={() => setSelectOpen(false)}
                      onChange={(event) => {
                        setSelectOpen(false);
                        handleBlockChange(event);
                      }}
                      disabled={!filters.selectedParker || loadingHorarios}
                      label="Seleccione el bloque horario"
                      displayEmpty
                      renderValue={(selected) => {
                        if (!selected || !filters.selectedDay) return "Seleccione el bloque horario";

                        const [startHourStr, endHourStr] = selected.split('-');
                        const startHour = parseInt(startHourStr, 10);
                        const endHour = parseInt(endHourStr, 10);

                        const startDate = new Date(filters.selectedDay);
                        startDate.setHours(startHour, 0, 0, 0);

                        const endDate = new Date(filters.selectedDay);
                        endDate.setHours(endHour, 0, 0, 0);

                        if (endDate <= startDate) {
                          endDate.setDate(endDate.getDate() + 1);
                        }

                        const options = { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
                        
                        const formattedStart = startDate.toLocaleString('es-ES', options);
                        const formattedEnd = endDate.toLocaleString('es-ES', options);

                        return `${formattedStart} - ${formattedEnd}`;
                      }}
                    >
                      {loadingHorarios ? (
                        <MenuItem disabled>
                          <CircularProgress size={20} sx={{ mr: 1 }} />
                          Cargando horarios...
                        </MenuItem>
                      ) : (
                        <>
                          <MenuItem value="">
                            <em>Seleccione el bloque horario</em>
                          </MenuItem>
                          {availableStartTimes.length > 0 ? (
                            <>
                              {[6, 8, 10, 12, 14, 16, 18, 20]
                                .filter(h => availableStartTimes.includes(h))
                                .map(startHour => {
                                  const endHour = startHour + 2;
                                  const value = `${startHour}-${endHour}`;
                                  const label = `${String(startHour).padStart(2,'0')}:00 - ${String(endHour).padStart(2,'0')}:00`;
                                  return (
                                    <MenuItem 
                                      key={value} 
                                      value={value}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setSelectOpen(false);
                                        handleBlockChange({ target: { value } });
                                      }}
                                    >
                                      {label}
                                    </MenuItem>
                                  );
                                })}
                              {availableStartTimes.includes(20) && (
                                <MenuItem 
                                  value="22-6"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectOpen(false);
                                    handleBlockChange({ target: { value: "22-6" } });
                                  }}
                                >
                                  22:00 - 06:00 (Turno Nocturno)
                                </MenuItem>
                              )}
                            </>
                          ) : (
                            <MenuItem disabled>
                              No hay horarios disponibles para esta fecha y celda
                            </MenuItem>
                          )}
                        </>
                      )}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Términos y Condiciones */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      sx={{
                        color: '#002642',
                        '&.Mui-checked': {
                          color: '#002642',
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: '#75787B' }}>
                      Acepto los términos y condiciones de reserva
                    </Typography>
                  }
                />

                {/* Botón de Reservar */}
                <Button
                  onClick={handleReserveClick}
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={!filters.selectedDay || !filters.selectedStartTime || !filters.selectedEndTime || !filters.selectedParker || !filters.selectedVehicle || !termsAccepted || sinSaldo}
                  sx={{
                    backgroundColor: '#002642',
                    py: 2,
                    borderRadius: 2,
                    fontSize: '1rem',
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: '#00577A',
                    },
                    '&:disabled': {
                      backgroundColor: '#ccc',
                      color: '#999'
                    }
                  }}
                  startIcon={<CheckCircle />}
                >
                  Confirmar Reserva
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

const Reservas = () => {
  return (
    <Fade in timeout={800}>
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 4 } }}>
        {/* Header moderno */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: { xs: 1.5, sm: 2 }, 
          mb: { xs: 2.5, sm: 4 }
        }}>
          <Box
            sx={{
              p: { xs: 1.25, sm: 1.5 },
              borderRadius: { xs: 2, sm: 3 },
              background: 'linear-gradient(135deg, #002642 0%, #00577A 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <ElectricCar sx={{ 
              color: '#FFFFFF', 
              fontSize: { xs: '1.75rem', sm: '2rem' } 
            }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: '#002642',
                fontSize: { xs: '1.375rem', sm: '2rem', md: '2.5rem' },
                mb: { xs: 0.25, sm: 0.5 },
                lineHeight: { xs: 1.2, sm: 1.1 }
              }}
            >
              Reservar Cargador
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#75787B',
                fontSize: { xs: '0.8rem', sm: '1rem' },
                lineHeight: 1.3
              }}
            >
              Reserva tu espacio de carga eléctrica
            </Typography>
          </Box>
        </Box>

        {/* Content */}
        <MovilidadElectricaProvider>
          <ReservasContent />
        </MovilidadElectricaProvider>
      </Container>
    </Fade>
  );
};

export default Reservas;
